/* 
  NOTE 
  ---------------------- 
  1. Entire validation of any job tab occures in this file and all the validation functions are made tab specific.
  2. If new required variable is added in any of the tab, update `StateToField` object and make appropriate changes in tab's respective validation function 
  ---------------------- 
*/

import { SKIP_FILE_CUSTOM_VALUE_LIMIT } from "../../../../../util/Data/DataLoad";
import { regexName } from "../../../../../util/regularExpress";
import { JOB_PURPOSE_CONVERSION } from "../../utils/JobData";

// On Job Name tab name and data pipeline is required fields
const StateToField = {
  // Name Tab
  name: "Job Name",
  DataPipeline_id: "Pipeline",

  //   Data Source/Entity Tabe
  dataSets: {
    1: "Data Source",
    4: "Data Source",
    5: "Entity",
    6: "Entity",
  },

  //   Run time tab
  cluster: "Compute Cluster",
  userNotExist: "Run the job as",

  //   Common for Transform & Action Tab
  schema: "Schema",
  query: "Query",

  //   Transform tab
  Type: "Type",

  //   Action Tab
  source: "Source Connection",
  destination: "Destination Connection",
  sourcePath: "Source Path",
  targetPath: "Destination Folder Path",
  filename: "File Name",
  fileType: "File Type",
  delimiter: "Delimiter",
  customDelimeter: "Custom Delimiter",
  nullValue: "Null Values",
  customNullValue: "Custom Null Values",
  table: "Table",
  onError: "On Error",
  onErrorNumber: "On Error Threshold",
  jobPurpose: "Operation",
  conversionFrom: "Convert From",
  regexMatchPattern: "Match Pattern",
  regexReplacePattern: "Replace Pattern",

  objectAPIName: "Object API Name",
  operationType: "Operation Type",
  externalIdFieldName: "ID Field",

  //   Target Tab
  type: "Type",
  jobPipeline: "Target",

  // Notification Job
  subject: "Email Subject",
  body: "Email Body",
  emails: "Email Recipients",
  queryResultAs: "Query Result type",
  location: "Connection",
};

// If multiple values are passed then make array of messages else single string
const constructRequiredFieldMessage = (fields) => {
  if (Array.isArray(fields)) return fields.map((field) => `"${field}" is required.`);
  else return `"${fields}" is required.`;
};

// If multiple values are passed then make array of messages else single string
const constructInvalidMessage = (fields) => {
  if (Array.isArray(fields)) return fields.map((field) => `Invalid ${field}.`);
  else return `Invalid ${fields}.`;
};

// NAME TAB validation
const validateNameTab = async ({ name, DataPipeline_id }) => {
  const requiredFields = [];
  if (name.trim() === "") requiredFields.push(StateToField.name);
  if (!DataPipeline_id) requiredFields.push(StateToField.DataPipeline_id);
  return constructRequiredFieldMessage(requiredFields);
};

// DATA SOURCE/ENTITY TAB validation
const validateDataObjectTab = async (state, JobType) => {
  const { IsDataVaultEnabled, dataSets } = state.dataObject;
  const queryTab = state.queryTab.query;

  const hasQuery = JobType === 4 && !!queryTab;

  const errors = [];
  if (IsDataVaultEnabled && dataSets.every((object) => !object.checked) && !hasQuery)
    errors.push(`Atleast one ${StateToField.dataSets[JobType]} should be selected.`);
  return errors;
};

// RUNTIME TAB validation
const validateRunTimeTab = async (
  { cluster, runJobAsUserId, isExternalSnowflake = false, isSnowflake = false },
  JobType,
  location
) => {
  const requiredFields = [];

  if (!cluster && ([1, 3, 4, 5, 6].includes(JobType) || (JobType === 2 && isExternalSnowflake)))
    requiredFields.push(StateToField.cluster);
  if ([2, 3].includes(JobType) && !isExternalSnowflake && (!runJobAsUserId || runJobAsUserId === "")) {
    if ((JobType === 2 && isSnowflake) || (JobType === 3 && location === "SNOWFLAKE"))
      requiredFields.push(StateToField.userNotExist);
  }

  return constructRequiredFieldMessage(requiredFields);
};

// QUERY TAB validation
const validateQueryTab = async (queryError, JobType) => {
  const invalidFields = [];

  if (JobType === 4 && queryError) invalidFields.push(StateToField.query);

  return constructInvalidMessage(invalidFields);
};

// TRANSFORM/ROUTING TAB validation
const validateTransformTab = async ({ Type, query, queryError, database, schema, isSnowflake }) => {
  const requiredFields = [];
  const invalidFields = [];

  if (query.trim() === "") requiredFields.push(StateToField.query);
  if (queryError) invalidFields.push(StateToField.query);
  if (isSnowflake && (database === "" || schema === "")) requiredFields.push(StateToField.schema);
  if (!Type) requiredFields.push(StateToField.Type);

  return [...constructRequiredFieldMessage(requiredFields), ...constructInvalidMessage(invalidFields)];
};

// TRANSFORM TAB validation
const validateTargetTab = async ({ jobPipeline, type }) => {
  const requiredFields = [];

  if (!jobPipeline) requiredFields.push(StateToField.jobPipeline);
  if (!type) requiredFields.push(StateToField.type);

  return constructRequiredFieldMessage(requiredFields);
};

// ACTION TAB validation
const validateActionTab = async (state) => {
  const {
    source,
    destination,
    destinationType,
    sourceType,
    sourcePath,
    targetPath,
    sourcePathError,
    query,
    queryError,
    filename,
    fileType,
    delimiter,
    customDelimeter,
    schemaName,
    tableName,
    onError,
    onErrorNumber,
    nullValue,
    customNullValue,
    jobPurpose,
    conversionFrom,
    externalIdFieldName,
    isExternalSnowflake,
    renameFileRegex,
    regexMatchPattern,
    regexReplacePattern,
  } = state;

  const requiredFields = [];
  const invalidFields = [];

  if (!jobPurpose) requiredFields.push(StateToField.jobPurpose);
  if (jobPurpose === JOB_PURPOSE_CONVERSION && !conversionFrom) requiredFields.push(StateToField.conversionFrom);

  if (!source || source === "") requiredFields.push(StateToField.source);
  if (!destination || destination === "") requiredFields.push(StateToField.destination);

  if (
    renameFileRegex &&
    ((sourceType?.toLowerCase() === "s3" && (destinationType === "s3" || destinationType === "sftp")) ||
      (destinationType === "s3" && (sourceType?.toLowerCase() === "sftp" || sourceType?.toLowerCase() === "azure")))
  ) {
    if (!regexMatchPattern) requiredFields.push(StateToField.regexMatchPattern);
    if (!regexReplacePattern) requiredFields.push(StateToField.regexReplacePattern);
  }

  if (["sftp", "azure", "s3", "sharepoint"].includes(sourceType.toLowerCase())) {
    if (!sourcePath || sourcePath.trim() === "") requiredFields.push(StateToField.sourcePath);
    if (sourcePathError) invalidFields.push(StateToField.sourcePath);
  } else if (
    (sourceType && ["salesforce", "veevavault"].includes(sourceType.toLowerCase())) ||
    sourceType.toLowerCase() === "snowflake"
  ) {
    if (query.trim() === "") requiredFields.push(StateToField.query);
    if (queryError) invalidFields.push(StateToField.query);

    if (!["salesforce", "veevavault"].includes(destinationType)) {
      if (!filename.trim() || filename.trim().trim() === "") requiredFields.push(StateToField.filename);
      if (!fileType) requiredFields.push(StateToField.fileType);
      if (!delimiter) requiredFields.push(StateToField.delimiter);
      if (delimiter && delimiter === "custom" && !customDelimeter.trim())
        requiredFields.push(StateToField.customDelimeter);
    }
  }

  if (isExternalSnowflake) {
    if (!schemaName.trim()) requiredFields.push(StateToField.schema);
  } else if (!["snowflake", "salesforce", "veevavault", "mysql"].includes(destinationType?.toLowerCase())) {
    if (!targetPath.trim()) requiredFields.push(StateToField.targetPath);
  } else if (destinationType === "snowflake") {
    if (!schemaName.trim()) requiredFields.push(StateToField.schema);
    if (!tableName.trim()) requiredFields.push(StateToField.table);
    if (!fileType) requiredFields.push(StateToField.fileType);
    if (fileType && fileType === "csv") {
      if (!delimiter) requiredFields.push(StateToField.delimiter);
      if (delimiter === "custom" && !customDelimeter.trim()) requiredFields.push(StateToField.customDelimeter);
    }
    if (!nullValue) requiredFields.push(StateToField.nullValue);
    if (nullValue === "custom" && !customNullValue.trim()) requiredFields.push(StateToField.customNullValue);
    if (!onError) requiredFields.push(StateToField.onError);
    if (onError?.startsWith("SKIP_FILE_")) {
      if (!onErrorNumber && onErrorNumber !== 0) requiredFields.push(StateToField.onErrorNumber);
      if (onErrorNumber) {
        if (regexName(onErrorNumber, "numeric")) {
          const newVal = Number(onErrorNumber);
          if (newVal < SKIP_FILE_CUSTOM_VALUE_LIMIT[onError].min || newVal > SKIP_FILE_CUSTOM_VALUE_LIMIT[onError].max)
            invalidFields.push(StateToField.onErrorNumber);
        } else invalidFields.push(StateToField.onErrorNumber);
      }
    }
  } else if (["salesforce", "veevavault"].includes(destinationType?.toLowerCase())) {
    if (!delimiter) requiredFields.push(StateToField.delimiter);
    if (!tableName) requiredFields.push(StateToField.objectAPIName);
    if (!schemaName) requiredFields.push(StateToField.operationType);
    if (
      (["update", "upsert"].includes(schemaName) ||
        (destinationType?.toLowerCase() === "veevavault" && schemaName === "delete")) &&
      !externalIdFieldName
    )
      requiredFields.push(StateToField.externalIdFieldName);
  }

  return [...constructRequiredFieldMessage(requiredFields), ...constructInvalidMessage(invalidFields)];
};

const validateNotificationTab = (state) => {
  const requiredFields = [];
  const custom = [];

  if (!state.subject) requiredFields.push(StateToField.subject);
  if (!state.body) requiredFields.push(StateToField.body);
  if (!state.emails) requiredFields.push(StateToField.emails);

  return [...constructRequiredFieldMessage(requiredFields), ...custom];
};

const validateAdditionalDataTab = (state, body) => {
  const custom = [];

  for (const [index, section] of state.entries()) {
    const {
      includeResult,
      queryResultAs,
      location,
      database,
      schema,
      Cluster,
      runJobAsUserId,
      variable,
      query,
      queryError,
    } = section;

    const preText = `Section ${index + 1}:`;

    if (includeResult) {
      if (queryError) {
        custom.push(`${preText} ${StateToField.query} is invalid!`);
      }
      if (!query) {
        custom.push(`${preText} ${StateToField.query} is required!`);
      }
      if ([undefined, null].includes(queryResultAs)) {
        custom.push(`${preText} ${StateToField.queryResultAs} is required!`);
      }
      if (!location) {
        custom.push(`${preText} ${StateToField.location} is required!`);
      }
      if (!database || !schema) {
        custom.push(`${preText} ${StateToField.schema} is required!`);
      }
      if (!Cluster) {
        custom.push(`${preText} ${StateToField.cluster} is required!`);
      }
      if (location === "SNOWFLAKE" && (!runJobAsUserId || runJobAsUserId === "")) {
        custom.push(`${preText} ${StateToField.userNotExist} is required!`);
      }
      if (queryResultAs === 2 && !body.includes(variable)) {
        custom.push(`${variable} variable is required in the email body!`);
      }
    }
  }

  return custom;
};

const validate = async (state, JobType) => {
  const location = state.transformTab?.location;
  const errors = await Promise.all([
    validateNameTab(state.nameTab),
    [1, 4, 5, 6].includes(JobType) ? validateDataObjectTab(state, JobType) : [],
    JobType === 2 ? validateActionTab(state.actionTab) : [],
    JobType === 3 || JobType === 8 ? validateTransformTab(state.transformTab) : [],
    JobType === 7 ? validateTargetTab(state.targetTab) : [],
    JobType === 9 ? validateNotificationTab(state.notificationTab) : [],
    JobType === 9 ? validateAdditionalDataTab(state.additionalDataTab, state.notificationTab.body) : [],
    validateRunTimeTab(state.runtime, JobType, location),
    validateQueryTab(state.queryTab.queryError, JobType),
  ]);

  return errors.flat(1);
};

export { validate };
